import React from "react";
import { Shield } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Zeelium</h1>
            </Link>
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms of Service ("Terms") govern your access to and use of Zeelium's enterprise security intelligence platform. By accessing our website, requesting a demo, or using our services, you ("Customer," "you," or "your organization") agree to be bound by these Terms. These Terms constitute a legally binding agreement between your organization and Zeelium.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Enterprise Service License</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Subject to these Terms and any executed service agreement, Zeelium grants your organization a limited, non-exclusive, non-transferable license to access and use our enterprise security platform for your internal business operations. Your use of the platform is governed by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>The terms of your executed service agreement or subscription plan</li>
              <li>Any applicable service level agreements (SLAs)</li>
              <li>Our enterprise security and compliance standards</li>
              <li>Industry best practices for security tool usage</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may not reverse engineer, decompile, or attempt to extract the source code of our platform. Enterprise customers may require custom agreements and additional terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Enterprise Service Description</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Zeelium provides an enterprise-grade AI-powered security intelligence and vulnerability management platform designed for security teams. Our platform integrates with your existing security tools to provide unified visibility, risk-based prioritization, automated remediation, and comprehensive analytics.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Enterprise service terms, including service levels, support levels, and specific features, will be detailed in your executed service agreement. We reserve the right to modify, enhance, or update our platform features in accordance with your agreement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Enterprise Account Management and Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As an enterprise customer, your organization is responsible for:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Maintaining accurate organizational and administrative contact information</li>
              <li>Managing user access, permissions, and account security within your organization</li>
              <li>Ensuring compliance with your organization's security policies and procedures</li>
              <li>Properly configuring integrations with your security tools and systems</li>
              <li>Notifying Zeelium immediately of any security incidents or unauthorized access</li>
              <li>Ensuring all users in your organization comply with these Terms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Enterprise Acceptable Use Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your organization and all authorized users agree to use our enterprise platform only for legitimate security operations purposes. You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Violate any applicable laws, regulations, or industry compliance requirements</li>
              <li>Use the platform for any purpose other than enterprise security management</li>
              <li>Attempt to access, probe, or attack our infrastructure or other customers' data</li>
              <li>Interfere with, disrupt, or compromise the security or performance of the platform</li>
              <li>Share account credentials or allow unauthorized access to your organization's account</li>
              <li>Use the platform to process data outside of your authorized scope of use</li>
              <li>Reverse engineer, decompile, or attempt to extract proprietary algorithms or code</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The service and its original content, features, and functionality are and will remain the exclusive property of Zeelium and its licensors. The service is protected by copyright, trademark, and other laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Enterprise Service Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our enterprise platform is provided "as is" and "as available." While we strive for high availability and accuracy, Zeelium does not guarantee that the platform will be error-free, uninterrupted, or completely secure. Enterprise customers should maintain appropriate backup and security measures for their own systems and data.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your executed service agreement may include specific service level commitments and warranties that supersede this general disclaimer.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In no event shall Zeelium, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree to defend, indemnify, and hold harmless Zeelium and its licensee and licensors, and their employees, contractors, agents, officers and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us through our website's contact form or by requesting a demo.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

